import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowLeft, Check, Copy, ShieldAlert, X } from "lucide-react";
import { toast } from "sonner";
import { useWorkspace, useWsQuery } from "@/context/WorkspaceContext";
import { Chip, PageHeader, QueryState } from "@/components/common";

const READINESS = {
  configuration_required: ["amber", "Awaiting credentials"], ready_to_connect: ["slate", "Ready to connect · not yet verified"],
  needs_attention: ["amber", "Connected · needs attention"], operational: ["green", "Operational · verified"],
  not_implemented: ["slate", "Adapter not built yet"],
};

function PlannedRow({ g }) {
  return (
    <div id={g.family} data-testid={`setup-family-${g.family}`} className="py-3 flex flex-col md:flex-row md:items-center gap-2 md:gap-4 scroll-mt-24">
      <div className="md:w-56 shrink-0"><div className="text-sm text-[#F7FAFF]">{g.name}</div><div className="text-[11px] text-[#7C8BAD]">{g.category} · {g.channels.join(", ")}</div></div>
      <span data-testid={`setup-readiness-${g.family}`}><Chip tone="slate">Adapter not built yet</Chip></span>
      <div className="text-[11px] text-[#AAB6CC] flex-1">Will need: {g.approvals[0].detail}. Credential names: <span className="font-mono">{g.required_config.map((k) => k.name).join(", ")}</span></div>
    </div>
  );
}

function ConfigList({ title, items, optional }) {
  return (
    <div><div className="cq-muted-label mb-1.5">{title}</div>
      <ul className="space-y-1">{items.map((k) => (
        <li key={k.name} data-testid={`setup-config-${k.name}`} className="flex items-center gap-2 text-xs font-mono">
          {k.present ? <Check size={12} className="text-[#4BE3A4]" /> : <X size={12} className={optional ? "text-[#7C8BAD]" : "text-[#FF7792]"} />}
          <span className="text-[#D5DDEB]">{k.name}</span><span className="text-[#7C8BAD] font-sans">{k.present ? "saved" : optional ? "not set (optional)" : "missing"}</span>
        </li>))}</ul>
    </div>
  );
}

function FamilyGuide({ g }) {
  const [tone, label] = READINESS[g.readiness];
  const copy = () => navigator.clipboard.writeText(g.redirect_uri).then(() => toast.success("Redirect URI copied"));
  return (
    <section id={g.family} data-testid={`setup-family-${g.family}`} className="cq-card p-5 space-y-5 scroll-mt-24">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="font-display text-lg text-[#F7FAFF]">{g.name}</h2>
        <span data-testid={`setup-readiness-${g.family}`}><Chip tone={tone}>{label}</Chip></span>
        {g.api_version && <span className="font-mono text-[10px] text-[#7C8BAD]">API {g.api_version}</span>}
      </div>
      {g.blocker && <p data-testid={`setup-blocker-${g.family}`} className="text-sm text-[#F4B860]">Configuration required: {g.blocker}. Nothing connects until the credentials are added and a live authorization succeeds.</p>}
      <div><div className="cq-muted-label mb-1.5">Redirect URI · add this exactly in the provider console</div>
        <div className="flex items-center gap-2"><code data-testid={`setup-redirect-${g.family}`} className="text-xs bg-[#050816] border border-[#243153] rounded px-2 py-1 text-[#22E6F2] break-all">{g.redirect_uri}</code>
          <button data-testid={`setup-copy-redirect-${g.family}`} onClick={copy} className="cq-btn cq-btn-ghost !p-1.5" aria-label="Copy redirect URI"><Copy size={12} /></button></div></div>
      <div id={`${g.family}-credentials`} className="grid md:grid-cols-2 gap-5 scroll-mt-24">
        <ConfigList title="Required configuration" items={g.required_config} />
        <ConfigList title="Optional configuration" items={g.optional_config} optional />
      </div>
      <div id={`${g.family}-permissions`} className="grid md:grid-cols-2 gap-5 scroll-mt-24">
        <div><div className="cq-muted-label mb-1.5">Permissions requested (read-only, all required)</div>
          <ul className="space-y-1">{g.required_permissions.map((p) => <li key={p.scope} className="text-xs"><span className="font-mono text-[#D5DDEB] break-all">{p.scope}</span><span className="text-[#7C8BAD]"> · {p.used_by.join(", ")}</span></li>)}</ul></div>
        <div><div className="cq-muted-label mb-1.5">Never requested by CRESQEN</div>
          <ul className="space-y-1">{g.not_requested.map((p) => <li key={p} className="text-xs text-[#7C8BAD] line-through">{p}</li>)}</ul></div>
      </div>
      <div><div className="cq-muted-label mb-1.5">Provider approvals · confirm these in the {g.name} console (CRESQEN can't check them for you)</div>
        <ul className="space-y-2">{g.approvals.map((a) => <li key={a.key} data-testid={`setup-approval-${g.family}-${a.key}`} className="text-xs"><span className="text-[#F4B860]">{a.title}</span><span className="text-[#AAB6CC]"> · {a.detail}</span></li>)}</ul></div>
      <div><div className="cq-muted-label mb-1.5">Steps</div>
        <ol className="list-decimal pl-4 space-y-1 text-xs text-[#D5DDEB]">{g.steps.map((s) => <li key={s}>{s}</li>)}</ol></div>
    </section>
  );
}

export default function ChannelSetup() {
  const { can } = useWorkspace();
  const allowed = can("manage_channels");
  const q = useWsQuery("/channels/setup-guide", { enabled: allowed });
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash || !q.data) return;
    const el = document.getElementById(hash.slice(1));
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [hash, q.data]);
  return (
    <div className="space-y-6">
      <Link to="/channels" data-testid="setup-back-link" className="text-xs text-[#7C8BAD] inline-flex items-center gap-1 hover:text-[#22E6F2]"><ArrowLeft size={12} /> Channel Connection Centre</Link>
      <PageHeader eyebrow="Measure" title="Connector setup & approvals" description="What each platform needs before it's operational, based on what CRESQEN actually requests. Shows key names and whether they're saved, never their values." />
      <div className="flex gap-3 p-4 rounded-xl border border-[#F4B860]/25 bg-[#F4B860]/5 text-sm text-[#D5DDEB]" data-testid="setup-readonly-notice">
        <ShieldAlert size={16} className="text-[#F4B860] shrink-0 mt-0.5" />
        <span>Read-only. CRESQEN doesn't publish, launch ads, change audiences or budgets, or spend money. A platform counts as operational only after a real authorization has been tested.</span>
      </div>
      {!allowed ? <p data-testid="setup-forbidden" className="text-sm text-[#7C8BAD]">Only owners and admins can view connector setup.</p> : (
        <QueryState q={q}>{(data) => (<>
          {data.filter((g) => g.adapter_implemented).map((g) => <FamilyGuide key={g.family} g={g} />)}
          <section className="cq-card p-5" data-testid="setup-planned-registry">
            <h2 className="font-display text-lg text-[#F7FAFF]">Connector registry · not yet built</h2>
            <p className="text-xs text-[#7C8BAD] mt-1">These platforms have no adapter in CRESQEN yet, so there's no Connect button. Listed so you can prepare provider access in advance.</p>
            <div className="divide-y divide-[#172342] mt-2">{data.filter((g) => !g.adapter_implemented).map((g) => <PlannedRow key={g.family} g={g} />)}</div>
          </section>
        </>)}</QueryState>)}
    </div>
  );
}
